import { RoutePath } from 'enums/routePath';
import routePaths from 'pages/routePaths';
import { useCallback, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';
import { Entries } from 'utils/tsUtils';

import initialContext, { EventActionConfigKey } from './initialContext';
import { EventAction } from './types';

interface UseEventTrackerStateProps {
  filterName: string;
  forceTrack?: boolean;
}

const getFilterClickedRegistryKey = (filterName: string, pathname: string) => {
  return `${pathname}-${filterName}`;
};

const enableTracking = true;

const useEventTrackerState = () => {
  const { eventTrackerConfig } = initialContext;
  const { pathname } = useLocation();
  const hasClickedRegistryInit: Record<string, boolean> = {};
  const hasClickedRegistryRef = useRef(hasClickedRegistryInit);

  const entryRoutePaths = Object.entries(routePaths) as Entries<
    typeof routePaths
  >;

  const matchedRoutePath = entryRoutePaths.reduce(
    (prev, [routePath, routePathname]) => {
      if (prev) {
        return prev;
      }
      if (
        matchPath(pathname, {
          path: routePathname,
          exact: true,
        })
      ) {
        if (routePath === RoutePath.HOME && pathname !== '/') {
          return null;
        }

        return routePath || null;
      }
      return null;
    },
    null as RoutePath | null,
  );

  const trackEvent = useCallback(
    ({ eventAction }: { eventAction: EventAction }) => {
      const trackEventAction = ({
        eventTrackerConfigKey,
      }: {
        eventTrackerConfigKey?: EventActionConfigKey;
      }) => {
        if (!matchedRoutePath || !eventTrackerConfigKey) {
          return;
        }
        const config = eventTrackerConfig[matchedRoutePath];
        if (!config) {
          return;
        }
        const configAction = config[eventTrackerConfigKey];

        if (configAction && configAction[eventAction]) {
          if (enableTracking && window.gtag) {
            window.gtag('event', 'ui_interaction', {
              app_feature: matchedRoutePath,
              ui_interaction_type: eventAction,
              ui_interaction_name: eventTrackerConfigKey,
            });
          }
        }
      };
      return trackEventAction;
    },
    [eventTrackerConfig, matchedRoutePath],
  );

  const onChangeTrackEvent = useCallback(
    trackEvent({ eventAction: EventAction.CHANGE }),
    [trackEvent],
  );

  const onClickTrackEvent = useCallback(
    trackEvent({ eventAction: EventAction.CLICK }),
    [trackEvent],
  );

  const onSubmitTrackEvent = useCallback(
    trackEvent({ eventAction: EventAction.SUBMIT }),
    [trackEvent],
  );

  const onFilterClickTrackEvent = useCallback(
    ({ filterName }: UseEventTrackerStateProps) => {
      const hasClickedKey = getFilterClickedRegistryKey(filterName, pathname);

      hasClickedRegistryRef.current[hasClickedKey] = true;
      if (!matchedRoutePath) {
        return;
      }

      const config = eventTrackerConfig[matchedRoutePath];

      if (!config) {
        return;
      }

      if (config.globalFilterActions[EventAction.CLICK]) {
        if (enableTracking && window.gtag) {
          window.gtag('event', 'ui_interaction', {
            app_feature: matchedRoutePath,
            ui_interaction_type: 'click',
            ui_interaction_name: filterName,
          });
        }
      }
    },
    [eventTrackerConfig, matchedRoutePath, pathname],
  );

  const onFilterChangeTrackEvent = useCallback(
    ({ filterName, forceTrack = false }: UseEventTrackerStateProps) => {
      if (!matchedRoutePath) {
        return;
      }

      const config = eventTrackerConfig[matchedRoutePath];

      if (!config) {
        return;
      }

      const hasClickedKey = getFilterClickedRegistryKey(filterName, pathname);
      const hasClicked = hasClickedRegistryRef.current[hasClickedKey];

      if (
        (config.globalFilterActions[EventAction.CHANGE] && hasClicked) ||
        forceTrack
      ) {
        if (enableTracking && window.gtag) {
          window.gtag('event', 'ui_interaction', {
            app_feature: matchedRoutePath,
            ui_interaction_type: 'change',
            ui_interaction_name: filterName,
          });
        }

        hasClickedRegistryRef.current[hasClickedKey] = false;
      }
    },
    [eventTrackerConfig, matchedRoutePath, pathname],
  );
  return {
    eventTrackerConfig,
    onClickTrackEvent,
    onChangeTrackEvent,
    onSubmitTrackEvent,
    onFilterChangeTrackEvent,
    onFilterClickTrackEvent,
  };
};

export default useEventTrackerState;
